import React, { FC } from "react";
import { SanityPost } from "@/types/sanityPost";
import { PageNode } from "@/types/pageNode";
import { PortableText } from "@portabletext/react";

import { MainLayout } from "@/layouts";
import { SanityImage } from "@/components/SanityImage";
import { Seo } from "@/components/Seo/Seo";
import { BlogCta } from "@/views/IndexView/components/BlogCta";
import { Box, Container, Link, Stack, Typography } from "@mui/material";
import { PostMeta } from "@/components/PostMeta";
import { Styles } from "@/theme/theme.type";
import { ContentTable } from "@/components/ContentTable";
import { ShareWidget } from "@/components/ShareWidget";
import { AuthorBlock } from "@/components/AuthorBlock";
import { graphql, useStaticQuery } from "gatsby";

export const Head: FC<PageNode<SanityPost>> = ({
  pageContext: { title, readingTime, _rawSeo },
  location: { pathname }
}) => {
  const {
    site: {
      siteMetadata: { siteUrl }
    }
  } = useStaticQuery<{ site: { siteMetadata: { siteUrl: string } } }>(graphql`
    query {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `);

  return (
    <Seo
      rawSeo={_rawSeo}
      title={`${title} | 10 Speed TMS`}
      isArticle
      readingTime={readingTime}
      url={`${siteUrl}${pathname}`}
    />
  );
};

type Props = PageNode<SanityPost & { latest?: boolean }>;

type HeadingNode = [string] | [{ props: { text: string | null } }];

const BlogPost: FC<Props> = ({
  pageContext: {
    title,
    _rawBrief,
    _rawBody,
    readingTime,
    publishedAt,
    _rawMainImage,
    author,
    latest = false
  },
  location
}) => {
  const isHeadingNode = (children: any): children is HeadingNode => {
    const child = children[0];

    if (!Array.isArray(children) && !child) {
      return false;
    }

    return child && (typeof child === "string" || child.props.text);
  };

  const formHeadingSlug = (children: any) => {
    const child = children[0];
    const formatSlug = (text: string) =>
      text.toLocaleLowerCase().split(" ").join("-");

    if (!isHeadingNode(children)) {
      return;
    }

    if (typeof child === "string") {
      return formatSlug(child);
    }

    if (child.props.text) {
      return formatSlug(child.props.text);
    }
  };

  const styles = {
    imageContainer: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    heading: {
      fontWeight: 700,
      position: "relative",
      my: {
        xs: 3,
        sm: 6
      },
      "& > span": {
        position: "absolute",
        left: 0,
        top: { xs: -118, sm: -80 },
        visibility: "hidden",
        pointerEvents: "none"
      }
    },
    briefParagraph: {
      fontWeight: 600,
      color: ({ palette }) => palette.text.disabled,
      fontSize: { xs: "1.375rem", sm: "1.875rem" },
      lineHeight: { xs: 1.45, sm: 1.35 }
    },
    briefStrong: {
      lineHeight: "inherit",
      fontSize: "inherit",
      fontWeight: "inherit",
      color: ({ palette }) => palette.text.primary
    },
    list: {
      counterReset: "item",
      fontSize: { xs: "0.875rem", sm: "1rem" },
      lineHeight: { xs: 1.4, sm: 1.5 },
      pl: { xs: 3, sm: 5 },
      my: {
        xs: 2.5,
        sm: 3
      },
      "& > li": {
        counterIncrement: "item"
      },
      "& > li::marker": {
        fontWeight: 700,
        counterIncrement: "item",
        content: 'counters(item, "") "  "'
      },
      "& li ol, & li ul": {
        my: 1
      },
      "& li ol > li::marker, & li ul > li::marker": {
        content: 'counters(item, ".") " "'
      }
    }
  } satisfies Styles;

  return (
    <MainLayout>
      <Box
        sx={{
          mt: { xs: 0, sm: 11 },
          mb: { xs: 5, sm: 10 }
        }}
      >
        <Container
          sx={{
            maxWidth: { md: 848 },
            px: { xs: 0, sm: 3 }
          }}
        >
          <Box
            sx={{
              position: "relative",
              height: 400,
              mb: {
                xs: 3,
                sm: 6
              }
            }}
          >
            <SanityImage
              style={{ position: "absolute", inset: 0 }}
              imageAssetId={_rawMainImage}
              alt={`cover image for ${title}`}
            />
          </Box>
        </Container>
        <Container
          sx={{
            maxWidth: { md: 848 }
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
            mb={3}
          >
            <ShareWidget href={location.href} />

            <PostMeta
              latest={latest}
              publishedAt={publishedAt}
              readingTime={readingTime}
            />
          </Stack>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: "2rem", sm: "3rem" },
              fontWeight: 700,
              lineHeight: 1.25,
              mb: 3
            }}
          >
            {title}
          </Typography>
          {!!_rawBrief && (
            <PortableText
              value={_rawBrief}
              components={{
                marks: {
                  strong: ({ children }) => (
                    <Typography component="strong" sx={styles.briefStrong}>
                      {children}
                    </Typography>
                  )
                },
                block: {
                  normal: ({ children }) => (
                    <Typography sx={styles.briefParagraph}>
                      {children}
                    </Typography>
                  )
                }
              }}
            />
          )}

          <ContentTable content={_rawBody} />

          {!!_rawBody && (
            <PortableText
              value={_rawBody}
              components={{
                marks: {
                  link: ({ value, children }) => (
                    <Link
                      href={value.href}
                      sx={{
                        textDecoration: "none",
                        fontWeight: 700,
                        color: ({ palette }) => palette.primary.main
                      }}
                    >
                      {children}
                    </Link>
                  )
                },
                types: {
                  image: value => (
                    <Box sx={styles.imageContainer}>
                      <SanityImage
                        imageAssetId={value.value}
                        loading="lazy"
                        alt="picture"
                        imageAssetConfig={{
                          width: 1100
                        }}
                      />
                    </Box>
                  )
                },
                block: {
                  h2: ({ children }) => (
                    <Typography
                      component="h2"
                      sx={[
                        styles.heading,
                        {
                          fontSize: { xs: "1.5rem", sm: "2.5rem" },
                          lineHeight: { xs: 1.35, sm: 1.2 }
                        }
                      ]}
                    >
                      {children}
                      <Typography
                        component="span"
                        id={formHeadingSlug(children)}
                      />
                    </Typography>
                  ),
                  h3: ({ children }) => (
                    <Typography
                      component="h2"
                      sx={[
                        styles.heading,
                        {
                          fontSize: { xs: "1rem", sm: "1.5rem" },
                          lineHeight: { xs: 1.5, sm: 1.35 }
                        }
                      ]}
                    >
                      {children}
                      <Typography
                        component="span"
                        id={formHeadingSlug(children)}
                      />
                    </Typography>
                  ),
                  h4: ({ children }) => (
                    <Typography
                      component="h2"
                      sx={[
                        styles.heading,
                        {
                          fontSize: { xs: "0.75rem", sm: "1rem" },
                          lineHeight: { xs: 1.35, sm: 1.5 }
                        }
                      ]}
                    >
                      {children}
                      <Typography
                        component="span"
                        id={formHeadingSlug(children)}
                      />
                    </Typography>
                  ),
                  normal: ({ children }) => (
                    <Typography
                      sx={{
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        lineHeight: { xs: 1.4, sm: 1.5 },
                        my: {
                          xs: 2.5,
                          sm: 3
                        }
                      }}
                    >
                      {children}
                    </Typography>
                  )
                },
                list: {
                  bullet: ({ children }) => (
                    <Box component="ul" sx={styles.list}>
                      {children}
                    </Box>
                  ),
                  number: ({ children }) => (
                    <Box component="ol" sx={styles.list}>
                      {children}
                    </Box>
                  )
                }
              }}
            />
          )}
          {!!author && <AuthorBlock author={author} />}
        </Container>
      </Box>
      <BlogCta />
    </MainLayout>
  );
};

export default BlogPost;
